import AppFormContainer from '@nxsys/components/AppFormContainer';
import AppInput from '@nxsys/components/AppInput';
import { useTypedIntl } from '@nxsys/hooks/useTypedIntl';
import { AppState } from '@nxsys/store';
import { IRELAND } from '@nxsys/store/constants/keys';
import { Form, Radio } from 'antd';
import React, { Ref, useImperativeHandle } from 'react';
import { useSelector } from 'react-redux';

export type DeductionFormValues = {
  description?: string;
  deductionCode?: string;
  includeInCisCalculation?: boolean;
  beforeTax?: boolean;
  beforeNI?: boolean;
  beforePension?: boolean;
  taxCanBeDeducted?: boolean;
  employeePRSICanBeDeducted?: boolean;
  employerPRSICanBeDeducted?: boolean;
  uscCanBeDeducted?: boolean;
  ascCanBeDeducted?: boolean;
  pensionSchemaDeductionCanBeDeducted?: boolean;
  lptCanBeDeducted?: boolean;
};

export type DeductionFormRef = {
  submit: () => void;
};

type DeductionFormProps = {
  formRef?: Ref<DeductionFormRef>;
  onSubmit?: (values: DeductionFormValues) => void;
  defaultValues?: DeductionFormValues;
};

const DeductionForm: React.FC<DeductionFormProps> = ({
  formRef,
  onSubmit,
  defaultValues,
}) => {
  const { isBureau, payrollCountry } = useSelector(
    (state: AppState) => state.appStore,
  );
  const isIreland = payrollCountry === IRELAND;

  const { messages } = useTypedIntl();

  const [form] = Form.useForm<DeductionFormValues>();

  useImperativeHandle(formRef, () => ({
    submit() {
      form.submit();
    },
  }));

  const onFinish = (values: DeductionFormValues) => {
    onSubmit?.(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.error(errorInfo);
  };

  return (
    <AppFormContainer
      form={form}
      onFinish={onFinish}
      onFinishFailed={onFinishFailed}
      {...(defaultValues ? { initialValues: defaultValues } : {})}
    >
      {/* <Form.Item
        label={messages['paymentSetting.deduction.deductionCode']}
        name="deductionCode"
        required={true}
      >
        <AppInput placeholder={messages['form.autoPopulate']} disabled />
      </Form.Item> */}

      <Form.Item
        label={messages['paymentSetting.payment.description']}
        name="description"
        rules={[
          {
            required: true,
            message: messages['form.pleaseEnter'],
          },
        ]}
      >
        <AppInput placeholder={messages['form.enterDescription']} />
      </Form.Item>
      {!isIreland ? (
        <>
          {!isBureau && (
            <Form.Item
              label={messages['paymentSetting.payment.includeInCisCalculation']}
              name="includeInCisCalculation"
              rules={[
                {
                  required: true,
                  message: messages['form.pleaseSelect'],
                },
              ]}
            >
              <Radio.Group>
                <Radio value={true}>{messages['common.yes']}</Radio>
                <Radio value={false}>{messages['common.no']}</Radio>
              </Radio.Group>
            </Form.Item>
          )}

          <Form.Item
            label={messages['paymentSetting.payment.beforeTax']}
            name="beforeTax"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={messages['paymentSetting.payment.beforeNI']}
            name="beforeNI"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            label={messages['paymentSetting.payment.beforePension']}
            name="beforePension"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
        </>
      ) : (
        <>
          <Form.Item
            label={messages['paymentSetting.payment.beforeTax']}
            name="taxCanBeDeducted"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={messages['paymentSetting.payment.beforeEmployeePRSI']}
            name="employeePRSICanBeDeducted"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={messages['paymentSetting.payment.beforeEmployerPRSI']}
            name="employerPRSICanBeDeducted"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={messages['paymentSetting.payment.beforeUSC']}
            name="uscCanBeDeducted"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={messages['paymentSetting.payment.beforeASC']}
            name="ascCanBeDeducted"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={
              messages['paymentSetting.payment.beforePensionSchemeDeductions']
            }
            name="pensionSchemaDeductionCanBeDeducted"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={messages['paymentSetting.payment.beforeLPT']}
            name="lptCanBeDeducted"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
          <Form.Item
            label={messages['paymentSetting.payment.beforeRCT']}
            name="includeInCisCalculation"
            rules={[
              {
                required: true,
                message: messages['form.pleaseSelect'],
              },
            ]}
          >
            <Radio.Group>
              <Radio value={true}>{messages['common.yes']}</Radio>
              <Radio value={false}>{messages['common.no']}</Radio>
            </Radio.Group>
          </Form.Item>
        </>
      )}
    </AppFormContainer>
  );
};

export default DeductionForm;
